import { decryptWithECDH } from '../utils/encryption';
import type { LocalIdentity } from '../types/identity';
import type { MsgChiffre, MsgCle } from '../types/message';

const ECDH_ALGO = 'AES-GCM-ECDH';

/**
 * Try to decrypt a message using ECDH keys.
 * Uses identity private key + each key's df_ecdh_scannable (sender public key).
 * Returns parsed JSON or null if no key decrypted.
 */
export async function tryDecryptWithKeys(
  msg: MsgChiffre,
  keys: MsgCle[],
  identity: LocalIdentity,
): Promise<unknown | null> {
  const pk = identity.privateKey;
  if (pk === undefined) {
    return null;
  }
  for (const kc of keys) {
    const algo = kc.cle_de_chiffrement_message?.algo;
    const params = kc.cle_de_chiffrement_message?.params as { iv?: string } | undefined;
    const iv = params?.iv;
    const senderPub = kc.df_ecdh_scannable;
    if (algo !== ECDH_ALGO || iv === undefined || senderPub === undefined) {
      continue;
    }
    try {
      const decrypted = await decryptWithECDH(
        msg.message_chiffre,
        iv,
        senderPub,
        pk,
      );
      return JSON.parse(decrypted) as unknown;
    } catch {
      continue;
    }
  }
  return null;
}
